import { useNavigate } from "react-router-dom";
import { Button } from "@satyrsmc/shared/components";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useWizard } from "./WizardContext";

interface WizardStep {
  label: string;
}

interface WizardShellProps {
  eventId: string;
  steps: WizardStep[];
  canAdvance: boolean;
  onNext: () => void;
  onBack: () => void;
  children: React.ReactNode;
  submitted: boolean;
}

export function WizardShell({
  eventId,
  steps,
  canAdvance,
  onNext,
  onBack,
  children,
  submitted,
}: WizardShellProps) {
  const { state } = useWizard();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();
  const currentStep = state.step;
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const handleBackToEvent = async () => {
    if (submitted) {
      navigate(`/events/${eventId}`);
      return;
    }
    const ok = await confirm("Your progress will be lost.", "Cancel registration?");
    if (ok) navigate(`/events/${eventId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Register</h1>
        <button
          type="button"
          onClick={handleBackToEvent}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Back to event
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {steps.map((step, i) => {
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          return (
            <div key={step.label} className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : isComplete
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {i + 1}
              </div>
              <span className={`text-sm ${isActive ? "font-medium" : "text-muted-foreground"}`}>
                {step.label}
              </span>
              {i < steps.length - 1 && <div className="h-px w-4 bg-border" />}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div>{children}</div>

      {/* Navigation */}
      {!isLast && (
        <div className="flex gap-2">
          {!isFirst && (
            <Button variant="ghost" onClick={onBack} className="flex-1">
              Back
            </Button>
          )}
          <Button className={isFirst ? "w-full" : "flex-1"} disabled={!canAdvance} onClick={onNext}>
            Next
          </Button>
        </div>
      )}

      {confirmDialog}
    </div>
  );
}
